<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
  <form @submit.prevent="createProject">
    <b-loading :active="loading" :is-full-page="false" />

    <template v-if="!loading">
      <cytomine-modal :active="active" :title="$t('create-case')" @close="$emit('update:active', false)">
        <div class="section">
          <h2 class="subtitle section-title">Case</h2>

          <b-field label="Project name" class="field-spacing">
            <b-input v-model="projectName" placeholder="Project name" @input="syncDefaultCaseIdentifiers" />
          </b-field>

          <b-field label="Case ID" class="field-spacing">
            <b-input v-model="caseId" placeholder="Case ID" @input="caseIdEdited = true" />
          </b-field>

          <b-field :label="$t('accession-id')" class="field-spacing">
            <b-input v-model="accessionId" :placeholder="$t('accession-id')" @input="accessionIdEdited = true" />
          </b-field>

          <b-field :label="$t('review')" class="field-spacing">
            <b-select v-model="projectStatusCode" expanded>
              <option v-for="option in reviewStatusOptions" :key="`review-${option.id}`" :value="option.id">
                {{ option.name }}
              </option>
            </b-select>
          </b-field>

          <b-field label="QC" class="field-spacing">
            <b-select v-model="qcStatusCode" expanded>
              <option v-for="option in reviewStatusOptions" :key="`qc-${option.id}`" :value="option.id">
                {{ option.name }}
              </option>
            </b-select>
          </b-field>
        </div>

        <div class="section">
          <h2 class="subtitle section-title">Specimen</h2>

          <b-field :label="$t('tissue')" class="field-spacing">
            <b-input v-model="tissue" :placeholder="$t('tissue')" />
          </b-field>

          <b-field :label="$t('stain')" class="field-spacing">
            <b-input v-model="stain" :placeholder="$t('stain')" />
          </b-field>

          <b-field :label="$t('specimen')" class="field-spacing">
            <b-input v-model="specimen" :placeholder="$t('specimen')" />
          </b-field>
        </div>

        <div class="section">
          <h2 class="subtitle section-title">{{ $t('patient-information') }}</h2>

          <b-field :label="$t('patient-id')" class="field-spacing">
            <b-input v-model="patientId" :placeholder="$t('patient-id')" />
          </b-field>

          <b-field :label="$t('patient-name')" class="field-spacing">
            <b-input v-model="patientName" :placeholder="$t('patient-name')" />
          </b-field>

          <b-field :label="$t('patient-age')" class="field-spacing">
            <b-input v-model="patientAge" type="number" :placeholder="$t('patient-age')" />
          </b-field>

          <b-field :label="$t('patient-sex')" class="field-spacing">
            <b-input v-model="patientSex" :placeholder="$t('patient-sex')" />
          </b-field>

          <b-field :label="$t('medical-record-number')" class="field-spacing">
            <b-input v-model="medicalRecordNumber" :placeholder="$t('medical-record-number')" />
          </b-field>

          <b-field :label="$t('access-date')" class="field-spacing">
            <b-datepicker v-model="accessDate" :placeholder="$t('access-date')" icon="calendar-today"
              :mobile-native="true" expanded />
          </b-field>
        </div>

        <template #footer>
          <button class="button" type="button" @click="$emit('update:active', false)">
            {{ $t('button-cancel') }}
          </button>
          <button class="button is-link" type="button" :disabled="errors.any()" @click="createProject">
            {{ $t('button-save') }}
          </button>
        </template>
      </cytomine-modal>
    </template>
  </form>
</template>

<script>
import { Project } from '@/api';

import CytomineModal from '@/components/utils/CytomineModal';
import { projectReviewStatusOptions } from '@/components/project/project-list-formatters';

export default {
  name: 'add-project-modal',
  props: {
    active: Boolean,
    ontologies: Array
  },
  components: { CytomineModal },
  $_veeValidate: { validator: 'new' },
  data() {
    return {
      loading: false,
      projectName: '',
      caseId: '',
      caseIdEdited: false,
      ontology: 'NEW',
      selectedOntology: null,

      // 新增字段
      patientId: '',
      patientName: '',
      patientAge: null,
      accessionId: '',
      medicalRecordNumber: '',
      accessDate: null,
      tissue: '',
      specimen: '',
      patientSex: '',
      stain: '',
      projectType: 'CLINICAL',
      projectStatusCode: 0,
      qcStatusCode: 0,
      accessionIdEdited: false
    };
  },
  computed: {
    reviewStatusOptions() {
      return projectReviewStatusOptions();
    }
  },
  watch: {
    active(val) {
      if (val) {
        this.projectName = '';
        this.caseId = '';
        this.caseIdEdited = false;
        this.ontology = 'NEW';
        this.selectedOntology = null;

        // 重置新增字段
        this.patientId = '';
        this.patientName = '';
        this.patientAge = null;
        this.accessionId = '';
        this.medicalRecordNumber = '';
        this.accessDate = null;
        this.tissue = '';
        this.specimen = '';
        this.patientSex = '';
        this.stain = '';
        this.projectType = 'CLINICAL';
        this.projectStatusCode = 0;
        this.qcStatusCode = 0;
        this.accessionIdEdited = false;
      }
    }
  },
  methods: {
    syncDefaultCaseIdentifiers() {
      if (!this.caseIdEdited) {
        this.caseId = this.projectName;
      }
      if (!this.accessionIdEdited) {
        this.accessionId = this.projectName;
      }
    },

    async createProject() {
      let result = await this.$validator.validateAll();
      if (!result) {
        return;
      }

      this.loading = true;
      try {
        let idOntology;
        // if (this.ontology === 'NEW') {
        //   let ontology = await new Ontology({ name: this.name }).save();
        //   idOntology = ontology.id;
        // } else if (this.ontology === 'EXISTING') {
        //   idOntology = this.selectedOntology;
        // }

        // 创建项目时包含新字段
        let projectData = {
          name: this.projectName,
          ontology: idOntology,
          patientId: this.patientId,
          patientName: this.patientName,
          patientAge: this.patientAge,
          caseId: this.caseId || this.projectName,
          accessionId: this.accessionId || this.projectName,
          medicalRecordNumber: this.medicalRecordNumber,
          accessDate: this.accessDate,
          tissue: this.tissue,
          specimen: this.specimen,
          patientSex: this.patientSex,
          stain: this.stain,
          type: this.projectType,
          statusCode: this.projectStatusCode,
          qcStatusCode: this.qcStatusCode,
          isReadOnly: false,
          isRestricted: true,
        };

        await new Project(projectData).save();

        this.loading = false;
        this.$notify({ type: 'success', text: this.$t('notif-success-case-created') });
        this.$emit('update:active', false);
        // await this.$router.push(`/project/${project.id}/configuration`);
      } catch (error) {
        if (error.response && error.response.status === 409) {
          this.$notify({ type: 'error', text: this.$t('notif-error-project-already-exists') });
          this.loading = false;
        } else {
          this.$notify({ type: 'error', text: this.$t('notif-error-project-creation') });
          this.loading = false;
        }
      }
    }
  },
};
</script>

<style scoped lang="scss">
.section {
  padding: 1rem 1rem;
}

.section:not(:last-child) {
  border-bottom: 1px solid rgba(190, 214, 238, 0.12);
}
</style>
